import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
	Typography,
	Box,
	IconButton,
	Stack,
	Grid,
	Divider,
	FormControl,
	FormLabel,
	useTheme,
	useMediaQuery,
	Button,
	FormHelperText,
	Tooltip,
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { DataGrid, GridToolbarContainer } from '@mui/x-data-grid';
import RefreshIcon from '@mui/icons-material/Refresh';
import ClearIcon from '@mui/icons-material/Clear';
import SearchField from '@/components/SearchField';
import { useGetBrokerGroupDetailsQuery, useGetUserDetailsQuery } from '@/features/user/userApi';
import { useGetInsightPoliciesInfiniteQuery } from '@/features/policies/policiesApi';
import InfiniteTablePagination from '../InfiniteTablePagination';
import { addDays } from 'date-fns';
import { setSelected } from '@/features/table/dealsTableSlice';
import { chunk } from 'lodash';
import { OwnerAutocomplete } from '@/components/selects/OwnerSelect';
import Fuse from 'fuse.js';
import { getComparator, stableSort } from '@/components/table/functions';
import { PresetDateRangeSelect } from '@/components/selects/PresetDateRangeSelect';
import PolicyTotalsHeader from './PolicyTotalsHeader';
import { CLIENT_PAGE_SIZE, columns as dataColumns, FUSE_OPTIONS } from './data';
import { useFeatureFlagContext } from '@/context/featureFlagContext';
import { NoRowsErrorOverlay, NoRowsOverlay } from '../../table/DataGridComponents/NoRowsOverlay';
import {
	setFromDate,
	setToDate,
	setOwnerIds,
	resetFilters,
	setBranchIds,
} from '@/features/table/renewalReportTableSlice';
import { parseToInsightDateString, safelyParseDate } from '@/utils/date';
import { useGridItemProps } from '@/components/table/filterGrid';
import { FilterAltOffOutlined } from '@mui/icons-material';
import {
	ColumnControlContextProvider,
	useDataGridColumnProps,
} from '@/context/columnControlContext.jsx';
import DataGridColumnMenu from '../../table/DataGridComponents/DataGridColumnMenu';
import DataGridColumnReorderButton from '@/components/table/DataGridComponents/DataGridColumnReorderButton';
import InsightFieldSelect from '@/components/selects/InsightFieldSelect';

const InsightPolicyTableFilters = ({ isInitialLoading, searchParams = {} }) => {
	const {
		searchText,
		setSearchText,
		searchValue,
		setSearchValue,
		setPaginationModel,
		disabled: searchDisabled,
	} = searchParams;

	const dispatch = useDispatch();

	const filters = useSelector((state) => state.renewalReportPrefs);

	const { username: email } = useSelector((state) => state.msalAccount.account);

	const { data: userDetails } = useGetUserDetailsQuery(
		{ email },
		{
			skip: !email,
		},
	);
	const canViewGroup = userDetails?.canViewGroup;

	const { getFeatureFlags } = useFeatureFlagContext();
	const renewalReportShowAll = Boolean(getFeatureFlags('renewalReportAllOption')?.active);

	const { searchGridProps, generalGridProps, clearFilterGridProps } = useGridItemProps();

	const handleReset = () => {
		setSearchText('');
		setSearchValue('');
	};

	const handleSearch = () => {
		if (searchText != searchValue) {
			setPaginationModel((prev) => ({ ...prev, page: 0 }));
			setSearchValue(searchText);
		}
	};
	return (
		<Grid
			container
			spacing={1}
			sx={{
				width: '100%',
			}}
		>
			<Grid
				container
				spacing={2}
				width='100%'
				sx={{
					'& .MuiFormControl-root': {
						width: '100%',
					},
				}}
			>
				<Grid {...searchGridProps}>
					<FormControl fullWidth>
						<FormLabel>Search</FormLabel>
						<SearchField
							value={searchText}
							disabled={searchDisabled}
							onKeyDown={(e) => {
								if (e.key === 'Enter') {
									handleSearch();
								}
							}}
							onChange={(e) => setSearchText(e.target.value)}
							onClick={handleSearch}
							onReset={handleReset}
						/>
					</FormControl>
				</Grid>
				{canViewGroup && (
					<>
						<Grid {...generalGridProps}>
							<FormControl fullWidth>
								<FormLabel>Client Owner</FormLabel>
								<OwnerAutocomplete
									includeInactive
									multiple
									userId={filters?.ownerIds}
									disabled={isInitialLoading || !userDetails?.canViewGroup}
									onChange={(newUsers) => {
										dispatch(setOwnerIds(newUsers));
									}}
									returnValue='id'
								/>
								<FormHelperText>&nbsp;</FormHelperText>
							</FormControl>
						</Grid>
						<Grid {...generalGridProps}>
							<FormControl fullWidth>
								<FormLabel>Branch</FormLabel>
								<InsightFieldSelect
									multiple
									value={filters.branchIds}
									onChange={(branchIds) => dispatch(setBranchIds(branchIds))}
									fieldType={'Branch'}
									disabled={isInitialLoading}
									clearable={renewalReportShowAll}
									showAllOption
								/>
								<FormHelperText>&nbsp;</FormHelperText>
							</FormControl>
						</Grid>
					</>
				)}

				<Grid {...generalGridProps}>
					<FormControl fullWidth>
						<FormLabel>End date</FormLabel>
						<PresetDateRangeSelect
							disabled={isInitialLoading}
							from={filters?.fromDate ? new Date(filters.fromDate) : null}
							to={filters?.toDate ? new Date(filters.toDate) : null}
							onChange={(nextFrom, nextTo) => {
								dispatch(setFromDate(nextFrom ? nextFrom.toISOString() : null));
								dispatch(setToDate(nextTo ? nextTo.toISOString() : null));
							}}
						/>
						<FormHelperText>&nbsp;</FormHelperText>
					</FormControl>
				</Grid>

				<Grid {...clearFilterGridProps}>
					<Tooltip title='Clear filters'>
						<IconButton
							sx={{
								mb: 3,
							}}
							// sx={{ width: '100%', minHeight: '40px', borderRadius: '6px' }}
							variant='text'
							size='small'
							color='primary'
							onClick={() => dispatch(resetFilters())}
						>
							<FilterAltOffOutlined />
						</IconButton>
					</Tooltip>
				</Grid>
			</Grid>
		</Grid>
	);
};
const InsightPolicyToolbar = ({ onRefresh, refreshColor }) => (
	<GridToolbarContainer sx={{ px: 1 }}>
		<DataGridColumnReorderButton />
		<Box sx={{ flexGrow: 1 }} />
		<IconButton size='small' onClick={onRefresh}>
			<RefreshIcon sx={{ fontSize: '1.25rem', color: refreshColor }} />
		</IconButton>
	</GridToolbarContainer>
);

const InsightPolicyTableContent = () => {
	const dispatch = useDispatch();
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
	const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));

	const filters = useSelector((state) => state.renewalReportPrefs);

	const [searchText, setSearchText] = useState('');
	const [searchValue, setSearchValue] = useState('');
	const [refreshIndicator, setRefreshIndicator] = useState(0);

	const { selected } = useSelector((state) => state.dealTable);

	const { username: email } = useSelector((state) => state.msalAccount.account);

	const endDateFromString = parseToInsightDateString(filters?.fromDate);

	const { data: userDetails, ...userQuery } = useGetUserDetailsQuery(
		{ email },
		{
			skip: !email,
		},
	);

	const { getFeatureFlags } = useFeatureFlagContext();
	const showRenewalAmountColumn = Boolean(getFeatureFlags('showRenewalAmountColumn')?.active);
	const renewalReportAllOption = Boolean(getFeatureFlags('renewalReportAllOption')?.active);

	useEffect(() => {
		if (!filters?.fromDate) {
			dispatch(setFromDate(addDays(new Date(), -90).toISOString()));
		}
		if (!filters?.toDate) {
			dispatch(setToDate(addDays(new Date(), 90).toISOString()));
		}
		if (
			filters.branchIds.length === 0 &&
			!renewalReportAllOption &&
			userDetails?.broker?.branchId
		) {
			dispatch(setBranchIds([userDetails.broker.branchId]));
		}
	}, [
		filters?.fromDate,
		filters?.toDate,
		filters.branchIds,
		userDetails?.broker?.branchId,
		renewalReportAllOption,
	]);

	const endDateToString = parseToInsightDateString(filters?.toDate);

	const columns = useMemo(
		() =>
			dataColumns
				.filter((column) => {
					if (!showRenewalAmountColumn && column.field === 'Balance') {
						return false;
					}
					if (!renewalReportAllOption && column.field === 'BranchId') {
						return false;
					}
					return true;
				})
				.map((column) => ({ ...column, disableColumnMenu: !column.hideable })),
		[showRenewalAmountColumn, renewalReportAllOption],
	);

	const {
		columns: orderedColumns,
		columnVisibilityModel,
		onColumnVisibilityModelChange: handleColumnVisibilityModelChange,
		onColumnWidthChange: handleColumnWidthChange,
	} = useDataGridColumnProps(columns);

	const [paginationModel, setPaginationModel] = useState({
		pageSize: CLIENT_PAGE_SIZE,
		page: 0,
	});

	const [sortModel, setSortModel] = useState([
		{
			field: 'RenewalDate',
			sort: 'asc',
		},
	]);

	const { data, fetchNextPage, hasNextPage, ...infiniteQuery } =
		useGetInsightPoliciesInfiniteQuery(
			{
				fromDate: endDateFromString,
				toDate: endDateToString,
				...(filters.branchIds.length > 0 && { branchIds: filters.branchIds }),
				ownerDocIds: filters.ownerIds,
				statusId: 0,
				typeId: 0,
			},
			{
				skip:
					!userDetails ||
					userQuery.isFetching ||
					(filters.branchIds.length === 0 && !renewalReportAllOption),
			},
		);

	const allPageData = [...(data?.pages.flatMap((r) => r.Results ?? []) ?? [])].map((r) => ({
		...r,
		...(r.ClientDocId && { clientDocId: r.ClientDocId }),
		...(r.OwnerDocId && { ownerDocId: r.OwnerDocId }),
		RenewalDate: safelyParseDate(r?.PolicyToDate ?? r?.ToDate)?.getTime?.(),
	}));
	const policiesIndex = useMemo(() => {
		if (allPageData.length > 0) {
			const fuseIndex = Fuse.createIndex(FUSE_OPTIONS.keys, allPageData);
			return new Fuse(allPageData, FUSE_OPTIONS, fuseIndex);
		}
	}, [allPageData]);

	const searchedPages = useMemo(() => {
		return (policiesIndex?.search(searchValue) ?? []).flatMap((v) => v?.item);
	}, [policiesIndex, searchValue]);

	const allPages = stableSort(
		(searchValue ? searchedPages : allPageData) ?? [],
		getComparator(sortModel[0].sort, sortModel[0].field, true),
	);

	const pageChunks = chunk(allPages ?? [], paginationModel.pageSize);
	const totalRecordCount = data?.pages?.[0]?.RecordCount;

	const count = useMemo(() => {
		if (searchValue || filters.ownerIds.length > 0) {
			return allPages.length;
		}
		if (totalRecordCount != null && hasNextPage) {
			return totalRecordCount;
		}
		return allPageData.length;
	}, [
		searchValue,
		filters.ownerIds,
		allPages.length,
		allPageData.length,
		totalRecordCount,
		hasNextPage,
	]);

	const displayedRows = useMemo(() => {
		if (!pageChunks.length) return [];
		return pageChunks[paginationModel.page] || [];
	}, [pageChunks, paginationModel.page]);

	useEffect(() => {
		setPaginationModel((prev) => ({ ...prev, page: 0 }));
	}, [searchValue, filters.ownerIds, filters.branchIds, filters.fromDate, filters.toDate]);

	useEffect(() => {
		let cancelled = false;

		const fetchServerPages = async () => {
			if (
				infiniteQuery.isUninitialized ||
				!data?.pages?.length ||
				!hasNextPage ||
				infiniteQuery.isFetching ||
				cancelled
			) {
				return;
			}

			await fetchNextPage();
		};

		fetchServerPages();

		return () => {
			cancelled = true;
		};
	}, [data?.pages?.length, hasNextPage, infiniteQuery.isFetching, infiniteQuery.isUninitialized]);

	const isInitialLoading =
		infiniteQuery.isLoading || (infiniteQuery.isFetching && !data?.pages?.length);

	const triggerRefresh = () => setRefreshIndicator((prev) => prev + 1);

	const handlePaginationModelChange = (newModel) => {
		setPaginationModel((prev) => ({ ...prev, page: newModel?.page }));
	};

	const handleSortModelChange = async (newModel) => {
		setPaginationModel((prev) => ({ ...prev, page: 0 }));
		setSortModel(newModel);
	};

	useEffect(() => {
		if (!infiniteQuery.isUninitialized && data) {
			infiniteQuery.refetch();
			if (paginationModel.page != 0) {
				setPaginationModel((prev) => ({ ...prev, page: 0 }));
			}
		}
		if (selected.length > 0) {
			dispatch(setSelected([]));
		}
	}, [refreshIndicator]);

	const rowSelectionModel = selected.map((policy) => policy.Id);

	const TablePagination = useCallback(() => {
		const totalClientPages = Math.ceil(allPages.length / paginationModel.pageSize);

		return (
			<InfiniteTablePagination
				{...{
					count,
					page: paginationModel.page,
					setPage: (page) => handlePaginationModelChange({ page }),
					rowsPerPage: paginationModel.pageSize,
					hasNextPage: paginationModel.page + 1 < totalClientPages,
					hasPreviousPage: paginationModel.page > 0,
				}}
			/>
		);
	}, [count, allPages, paginationModel.pageSize, paginationModel.page]);

	const dataGridKey = JSON.stringify({
		page: paginationModel.page,
		sort: sortModel,
		query: filters,
		dateFrom: endDateFromString,
		dateTo: endDateToString,
	});

	// get correct height of policy row for displaying loader
	return (
		<Stack sx={{ width: '100%' }} spacing={2}>
			<Box
				sx={{
					p: 1,
					border: `1px solid ${theme.palette.divider}`,
					borderRadius: '8px',
					backgroundColor: theme.palette.background.paper,
				}}
			>
				<PolicyTotalsHeader
					data={allPages}
					isLoading={isInitialLoading}
					isError={infiniteQuery.isError}
				/>
			</Box>
			<InsightPolicyTableFilters
				{...{ isInitialLoading }}
				searchParams={{
					searchText,
					setSearchText,
					searchValue,
					setSearchValue,
					setPaginationModel,
					disabled: isInitialLoading || rowSelectionModel.length > 0,
				}}
			/>

			{selected.length > 0 && (
				<Stack
					direction={{ xs: 'column', sm: 'row' }}
					spacing={1}
					sx={{
						justifyContent: 'space-between',
						alignItems: { xs: 'stretch', sm: 'center' },
					}}
				>
					<Box>
						<IconButton size='small' onClick={() => dispatch(setSelected([]))}>
							<ClearIcon />
						</IconButton>
						<Typography variant='task_updated'>{`${selected.length} policies selected`}</Typography>
					</Box>
				</Stack>
			)}
			<Box
				className='MuiDataGrid-parentDiv'
				sx={{
					display: 'flex',
					flexDirection: 'column',
					minHeight: '8em',
					maxHeight: 'calc(120vh - 300px)',
				}}
			>
				<DataGrid
					// loading={true}
					key={dataGridKey}
					loading={isInitialLoading}
					width='100%'
					// autoHeight
					getRowHeight={() => 'auto'}
					getRowId={(row) => String(row?.Id)}
					getRowClassName={(params) => {
						const { row } = params;
						return `row-policy-id-${row?.Id}`;
					}}
					initialState={{
						sorting: {
							sortModel,
						},
					}}
					sortingOrder={['desc', 'asc']}
					sx={{
						backgroundColor: theme.palette.background.paper,
						'& .MuiDataGrid-editInputCell': {
							backgroundColor: 'transparent',
						},
						'--DataGrid-overlayHeight': isInitialLoading ? '5em' : '3em',
						'& .MuiDataGrid-cell': {
							padding: 1,
							minHeight: '42px',
						},
						border: 'none',

						minHeight: '6em',
						height: isInitialLoading ? '6em' : 'auto',
						'& .MuiDataGrid-overlayWrapperInner': {
							minHeight: '5em',
						},
					}}
					isCellEditable={(params) => false}
					// cellModesModel={cellModesModel}
					// onCellModesModelChange={handleCellModesModelChange}
					// onProcessRowUpdateError={handleProcessRowUpdateError}
					rows={displayedRows}
					columns={orderedColumns}
					rowSelectionModel={rowSelectionModel}
					checkboxSelection={true}
					disableRowSelectionOnClick
					disableSelectionOnClick
					disableDensitySelector
					columnVisibilityModel={columnVisibilityModel}
					onColumnVisibilityModelChange={handleColumnVisibilityModelChange}
					onColumnWidthChange={handleColumnWidthChange}
					// disableColumnSorting
					slots={{
						noRowsOverlay: infiniteQuery.isError
							? () => <NoRowsErrorOverlay objectType='policies' />
							: () => <NoRowsOverlay objectType='policies' />,
						noResultsOverlay: infiniteQuery.isError
							? () => <NoRowsErrorOverlay objectType='policies' />
							: () => <NoRowsOverlay objectType='policies' />,
						toolbar: InsightPolicyToolbar,
						pagination: TablePagination,
						columnMenu: DataGridColumnMenu,
					}}
					slotProps={{
						loadingOverlay: {
							// variant: 'none'
							variant: 'skeleton',
							noRowsVariant: 'skeleton',
						},
						toolbar: {
							onRefresh: triggerRefresh,
							refreshColor: theme.palette.primary.main,
						},
					}}
					// pagination={false}
					paginationMode='server'
					filterMode='server'
					sortingMode='server'
					rowCount={count}
					paginationModel={paginationModel}
					sortModel={sortModel}
					onSortModelChange={handleSortModelChange}
					// pageSizeOptions={[10]}
				/>
			</Box>
			{/* <DevJSONView value={selectedRows} /> */}
		</Stack>
	);
};

const InsightPolicyTable = () => (
	<ColumnControlContextProvider variant='insightPolicy'>
		<InsightPolicyTableContent />
	</ColumnControlContextProvider>
);

export default InsightPolicyTable;
